import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const ChatbotMessageSchema = z
    .object({
        content: z.string().trim(),
        role: z.enum(["user", "assistant", "error"]),
    })
    .brand<"ChatbotMessage">();

export type ChatbotMessageInputProps = z.input<typeof ChatbotMessageSchema>;
type ChatbotMessageProps = z.output<typeof ChatbotMessageSchema>;

export class ChatbotMessage {
    private constructor(
        private readonly props: Readonly<ChatbotMessageProps>,
    ) {}

    get content(): string {
        return this.props.content;
    }

    get role(): "user" | "assistant" | "error" {
        return this.props.role;
    }

    static empty(): ChatbotMessage {
        return ChatbotMessage.create({
            content: "",
            role: "user",
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<ChatbotMessageInputProps>>,
    ): Result<ChatbotMessageProps, z.ZodError<ChatbotMessageProps>> {
        const result = ChatbotMessageSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<ChatbotMessageInputProps>>,
    ): Result<ChatbotMessageProps, PartialValid<typeof ChatbotMessage>> {
        const result = partialSafeParse(ChatbotMessageSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<ChatbotMessageInputProps>,
    ): Result<ChatbotMessage, z.ZodError<ChatbotMessageProps>> {
        return ChatbotMessage.validate(props).map(
            (validatedProps) => new ChatbotMessage(validatedProps),
        );
    }

    static createWithRecovery(
        props: ChatbotMessageInputProps,
    ): Result<ChatbotMessage, ModelError<typeof ChatbotMessage>> {
        return ChatbotMessage.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.content)
                    return new NonRecuperableModelError();

                const defaultProps: Unbrand<ChatbotMessageProps> = {
                    content: "",
                    role: "user",
                };

                const recoveryProps: Unbrand<ChatbotMessageProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof ChatbotMessage>(
                    new ChatbotMessage(recoveryProps as ChatbotMessageProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new ChatbotMessage(validatedProps));
    }

    toCreateProps(): ChatbotMessageInputProps {
        return {
            content: this.props.content,
            role: this.props.role,
        };
    }
}

const ChatbotMessagesSchema = z
    .object({
        chatbotMessages: z.array(
            z.custom<ChatbotMessage>(
                (value) => value instanceof ChatbotMessage,
                "Expected a ChatbotMessage instance",
            ),
        ),
        count: z.number().min(0),
    })
    .brand<"ChatbotMessages">();

type ChatbotMessagesInputProps = z.input<typeof ChatbotMessagesSchema>;
type ChatbotMessagesProps = z.output<typeof ChatbotMessagesSchema>;

export class ChatbotMessages {
    private constructor(
        private readonly props: Readonly<ChatbotMessagesProps>,
    ) {}

    get chatbotMessages(): readonly Readonly<ChatbotMessage>[] {
        return this.props.chatbotMessages;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): ChatbotMessages {
        return ChatbotMessages.create({
            chatbotMessages: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<ChatbotMessagesInputProps>>,
    ): Result<ChatbotMessagesProps, z.ZodError<ChatbotMessagesProps>> {
        const result = ChatbotMessagesSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<ChatbotMessagesInputProps>,
    ): Result<ChatbotMessages, z.ZodError<ChatbotMessagesProps>> {
        return ChatbotMessages.validate(props).map(
            (validatedProps) => new ChatbotMessages(validatedProps),
        );
    }
}

export const SpatialLevelUpdateSchema = z.object({
    id: z.number().positive(),
    name: z.string().trim().min(1),
});
